import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";

/**
 * MobileNav - Fixed bottom navigation bar for mobile
 */
const MobileNav = React.forwardRef(({ className, children, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (!isMobile) return null;

  return (
    <nav
      ref={ref}
      className={cn("mobile-nav", className)}
      role="navigation"
      aria-label="Mobile navigation"
      {...props}
    >
      {children}
    </nav>
  );
});
MobileNav.displayName = "MobileNav";

/**
 * MobileNavItem - Individual navigation item
 */
const MobileNavItem = React.forwardRef(
  (
    { className, icon: Icon, label, to, onClick, active, badge, ...props },
    ref,
  ) => {
    const location = useLocation();
    const isActive = active ?? (to && location.pathname === to);

    const content = (
      <>
        <span className="relative">
          {Icon && <Icon className="mobile-nav-item-icon" />}
          {badge !== undefined && badge > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium flex items-center justify-center">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </span>
        {label && <span className="mobile-nav-item-label">{label}</span>}
      </>
    );

    const itemClassName = cn(
      "mobile-nav-item",
      isActive && "active",
      className,
    );

    if (to) {
      return (
        <Link
          ref={ref}
          to={to}
          className={itemClassName}
          aria-current={isActive ? "page" : undefined}
          {...props}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        className={itemClassName}
        onClick={onClick}
        {...props}
      >
        {content}
      </button>
    );
  },
);
MobileNavItem.displayName = "MobileNavItem";

/**
 * MobileHeader - Sticky header for mobile pages
 */
const MobileHeader = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <header ref={ref} className={cn("sticky-header", className)} {...props}>
      <div className="flex items-center justify-between h-14 px-4">
        {children}
      </div>
    </header>
  ),
);
MobileHeader.displayName = "MobileHeader";

/**
 * MobileHeaderTitle - Title for mobile header
 */
const MobileHeaderTitle = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <h1
      ref={ref}
      className={cn("text-lg font-semibold truncate", className)}
      {...props}
    >
      {children}
    </h1>
  ),
);
MobileHeaderTitle.displayName = "MobileHeaderTitle";

/**
 * MobileHeaderActions - Actions container for mobile header
 */
const MobileHeaderActions = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center gap-1", className)}
      {...props}
    >
      {children}
    </div>
  ),
);
MobileHeaderActions.displayName = "MobileHeaderActions";

/**
 * MobileSearch - Mobile-optimized search input
 */
const MobileSearch = React.forwardRef(
  ({ className, icon: Icon, placeholder = "Search...", ...props }, ref) => (
    <div className={cn("mobile-search", className)}>
      {Icon && <Icon className="mobile-search-icon" />}
      <input
        ref={ref}
        type="search"
        placeholder={placeholder}
        className="mobile-search-input"
        {...props}
      />
    </div>
  ),
);
MobileSearch.displayName = "MobileSearch";

/**
 * MobilePageContainer - Container with proper padding for mobile nav
 */
const MobilePageContainer = React.forwardRef(
  ({ className, hasBottomNav = true, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "min-h-screen",
        hasBottomNav && "page-with-mobile-nav",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
MobilePageContainer.displayName = "MobilePageContainer";

/**
 * SegmentedControl - iOS-style segmented control
 */
const SegmentedControl = React.forwardRef(
  ({ className, value, onValueChange, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("segment-control", className)}
      role="tablist"
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return null;
        return React.cloneElement(child, {
          active: child.props.value === value,
          onClick: () => onValueChange?.(child.props.value),
        });
      })}
    </div>
  ),
);
SegmentedControl.displayName = "SegmentedControl";

/**
 * SegmentedControlItem - Individual segment
 */
const SegmentedControlItem = React.forwardRef(
  ({ className, value, active, children, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={active}
      className={cn("segment-control-item", active && "active", className)}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  ),
);
SegmentedControlItem.displayName = "SegmentedControlItem";

export {
  MobileNav,
  MobileNavItem,
  MobileHeader,
  MobileHeaderTitle,
  MobileHeaderActions,
  MobileSearch,
  MobilePageContainer,
  SegmentedControl,
  SegmentedControlItem,
};
